// Tabs Block - Group content into clickable tabs
class TabsBlock {
  static get toolbox() {
    return {
      title: 'Tabs',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="3" x2="9" y2="9"></line></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.data = {
      tabs: data.tabs || [
        { title: 'Tab 1', content: 'Content for tab 1' },
        { title: 'Tab 2', content: 'Content for tab 2' }
      ],
      activeTab: data.activeTab || 0
    };
    this.config = config;
    this.api = api;
    this.readOnly = readOnly;
    this.wrapper = null;
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('tabs-block');
    this._renderTabs();
    return this.wrapper;
  }

  _renderTabs() {
    this.wrapper.innerHTML = `
      <div class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="tabs-block">
        <div class="flex items-center bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <div class="flex-1 flex overflow-x-auto">
            ${this.data.tabs.map((tab, idx) => `
              <button 
                class="tab-btn px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors relative
                  ${idx === this.data.activeTab 
                    ? 'text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-900' 
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                  }"
                data-index="${idx}"
              >
                ${!this.readOnly ? `
                  <span class="tab-title" contenteditable="true" data-index="${idx}">${tab.title}</span>
                ` : tab.title}
                ${idx === this.data.activeTab ? `
                  <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                ` : ''}
              </button>
            `).join('')}
          </div>
          ${!this.readOnly ? `
            <div class="flex items-center gap-1 px-2">
              <button class="add-tab-btn p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Add tab">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
              ${this.data.tabs.length > 1 ? `
                <button class="remove-tab-btn p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Remove current tab">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              ` : ''}
            </div>
          ` : ''}
        </div>
        
        <div class="tab-content p-4 bg-white dark:bg-slate-900 min-h-[100px]">
          ${!this.readOnly ? `
            <div 
              class="content-editor text-slate-700 dark:text-slate-300 focus:outline-none min-h-[80px]"
              contenteditable="true"
              data-placeholder="Enter tab content..."
            >${this.data.tabs[this.data.activeTab]?.content || ''}</div>
          ` : `
            <div class="text-slate-700 dark:text-slate-300">${this.data.tabs[this.data.activeTab]?.content || 'No content'}</div>
          `}
        </div>
      </div>
    `;

    // Event listeners
    const tabButtons = this.wrapper.querySelectorAll('.tab-btn');
    tabButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (e.target.classList.contains('tab-title')) return;
        const index = parseInt(btn.dataset.index);
        this._switchTab(index);
      });
    });

    if (!this.readOnly) {
      // Tab title editing
      const tabTitles = this.wrapper.querySelectorAll('.tab-title');
      tabTitles.forEach(title => {
        title.addEventListener('input', (e) => {
          const index = parseInt(e.target.dataset.index);
          this.data.tabs[index].title = e.target.textContent;
        });
        title.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.target.blur();
          }
        });
      });

      // Content editing
      const contentEditor = this.wrapper.querySelector('.content-editor');
      if (contentEditor) {
        contentEditor.addEventListener('input', (e) => {
          this.data.tabs[this.data.activeTab].content = e.target.innerHTML;
        });
      }

      // Add tab button
      const addBtn = this.wrapper.querySelector('.add-tab-btn');
      if (addBtn) {
        addBtn.addEventListener('click', () => {
          this.data.tabs.push({ title: `Tab ${this.data.tabs.length + 1}`, content: '' });
          this.data.activeTab = this.data.tabs.length - 1;
          this._renderTabs();
        });
      }

      // Remove tab button
      const removeBtn = this.wrapper.querySelector('.remove-tab-btn');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          if (this.data.tabs.length > 1) {
            this.data.tabs.splice(this.data.activeTab, 1);
            this.data.activeTab = Math.min(this.data.activeTab, this.data.tabs.length - 1);
            this._renderTabs();
          }
        });
      }
    }
  }

  _switchTab(index) {
    // Save current content if editing
    if (!this.readOnly) {
      const contentEditor = this.wrapper.querySelector('.content-editor');
      if (contentEditor) {
        this.data.tabs[this.data.activeTab].content = contentEditor.innerHTML;
      }
    }
    
    this.data.activeTab = index;
    this._renderTabs();
  }

  save() {
    // Save current content before saving
    if (!this.readOnly) {
      const contentEditor = this.wrapper.querySelector('.content-editor');
      if (contentEditor) {
        this.data.tabs[this.data.activeTab].content = contentEditor.innerHTML;
      }
    }
    
    return {
      tabs: this.data.tabs,
      activeTab: this.data.activeTab
    };
  }

  static get sanitize() {
    return {
      tabs: {
        title: false,
        content: {
          br: true,
          b: true,
          i: true,
          a: { href: true },
          code: true
        }
      }
    };
  }
}

export default TabsBlock;
